import { ComponentSearchData } from 'viewpointBuilder/selectContextTab/types';
import {
  isArdoqError,
  type ArdoqError,
  type Result,
} from '@ardoq/common-helpers';
import {
  BooleanOperator,
  Operator,
  QueryBuilderQuery,
  QueryBuilderSubquery,
  SearchContext,
  SearchResultDoc,
  SortOrder,
} from '@ardoq/api-types';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { getIcon } from '@ardoq/icons';
import {
  ensureContrast,
  getDefaultCSSColor,
  getLightenedColor,
} from '@ardoq/color-helpers';
import { advancedSearchApi } from '@ardoq/api';
import { logError } from '@ardoq/logging';
import { advancedSearchComponentNameAndTypeAndStartSetQuery } from '@ardoq/query-builder';
import { orgUsersInterface } from 'modelInterface/orgUsers/orgUsersInterface';
import {
  AdvancedComponentSearchResponse,
  EnhancedRepresentationData,
} from './types';

const loadComponentsFromAdvancedSearch = async (
  componentName: string,
  componentTypeName: string,
  startSetFilter: QueryBuilderSubquery | null
): Promise<AdvancedComponentSearchResponse | ArdoqError> => {
  const requestBody = advancedSearchComponentNameAndTypeAndStartSetQuery(
    componentName,
    componentTypeName,
    startSetFilter
  );
  const response = await advancedSearchApi.search<
    SearchResultDoc & ComponentDoc
  >(requestBody, {
    sortBy: 'rank,name',
    sortOrder: SortOrder.ASC,
  });
  if (isArdoqError(response)) {
    return response;
  }

  return {
    totalCount: response.total,
    results: response.results.map(({ doc }) =>
      mapAdvancedSearchResultToComponentSearchData(doc)
    ),
  };
};

// While it might seem unusual to use the advanced search functionality to load
// components by ID, this approach aligns with the existing pattern of searching
// components by name, type, or other advanced search criteria. This ensures a
// consistent data format across different search types, including representation
// data and color. A typical use case for this function is to initiate a new
// traversal in the viewpoint builder with a specific set of component IDs, for
// instance, those selected from a context menu.
const loadComponentsByIdFromAdvancedSearch = async (
  componentIds: string[]
): Promise<AdvancedComponentSearchResponse> => {
  const requestBody =
    componentIdsToAdvancedSearchQueryRequestBody(componentIds);
  const response = await advancedSearchApi.search<
    SearchResultDoc & ComponentDoc
  >(requestBody);

  if (isArdoqError(response)) {
    logError(
      Error('Failed to fetch components by id with advanced search api')
    );
    return {
      totalCount: 0,
      results: [],
    };
  }

  return {
    totalCount: response.total,
    results: response.results.map(({ doc }) =>
      mapAdvancedSearchResultToComponentSearchData(doc)
    ),
  };
};

/**
 * Advanced search is permission filtered, so `null` means the component is
 * either deleted or invisible to the current user.
 */
export const findComponentById = async (
  componentId: string
): Promise<Result<ComponentSearchData | null>> => {
  const response = await advancedSearchApi.search<
    SearchResultDoc & ComponentDoc
  >(componentIdsToAdvancedSearchQueryRequestBody([componentId]));

  if (isArdoqError(response)) {
    return response;
  }

  const doc = response.results[0]?.doc;
  return doc ? mapAdvancedSearchResultToComponentSearchData(doc) : null;
};

const loadComponentsFromQueryBuilder = async (
  query: QueryBuilderQuery
): Promise<AdvancedComponentSearchResponse> => {
  const response = await advancedSearchApi.search<
    SearchResultDoc & ComponentDoc
  >(query, {
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
  });
  if (isArdoqError(response)) {
    logError(
      Error('Failed to fetch components by id with advanced search api')
    );
    return {
      totalCount: 0,
      results: [],
    };
  }
  return {
    totalCount: response.total,
    results: response.results.map(({ doc }) =>
      mapAdvancedSearchResultToComponentSearchData(doc)
    ),
  };
};

const componentIdsToAdvancedSearchQueryRequestBody = (
  componentIds: string[]
): QueryBuilderQuery => ({
  condition: BooleanOperator.AND,
  rules: [
    {
      id: 'type',
      field: 'type',
      type: 'string',
      input: 'select',
      operator: Operator.EQUAL,
      value: SearchContext.COMPONENT,
    },
    {
      condition: BooleanOperator.OR,
      rules: componentIds.map(id => ({
        id: '_id',
        field: '_id',
        input: 'text',
        type: 'string',
        operator: Operator.EQUAL,
        value: id,
      })),
    },
  ],
});

const mapAdvancedSearchResultToComponentSearchData = (
  doc: SearchResultDoc & ComponentDoc
): ComponentSearchData => ({
  name: doc.name,
  createdByName: getCreatedByName(doc),
  lastModifiedByName: getLastModifiedByName(doc),
  id: doc._id,
  entityType: doc.entityType,
  rootWorkspace: doc.rootWorkspace,
  rootWorkspaceName: doc.rootWorkspaceName,
  representationData:
    getComponentRepresentationDataAndColorFromSearchResult(doc),
  path: doc.path,
});

// The doc format returned by the advanced search endpoint is not really a
// subset of APIComponentAttributes. For the real product we will most
// likely have a dedicated new endpoint, so typing doc here on the fly.
export type ComponentDoc = {
  rootWorkspace: string;
  entityType: string;
  icon: string | null;
  image: string | null;
  color: string | null;
  rootWorkspaceName: string;
  lastModifiedBy: string;
  createdBy: string;
  path?: string[];
};

const getComponentRepresentationDataAndColorFromSearchResult = (
  doc: ComponentDoc
): EnhancedRepresentationData => {
  const {
    rootWorkspace,
    entityType: type,
    icon,
    image,
    color: componentColor,
  } = doc;
  let isImage = false;
  let value = null;
  let color = '#000';
  if (image) {
    isImage = true;
    value = image;
  } else if (icon) {
    value = icon;
  } else {
    const componentType = workspaceInterface
      .getComponentTypes(rootWorkspace)
      ?.find(({ name }) => name === type);
    if (componentType) {
      value = componentType.image || componentType.icon;
      isImage = Boolean(componentType.image);
      if (componentType.color) {
        color = componentType.color;
      } else {
        color = getDefaultCSSColor(componentType.level);
      }
    }
  }
  if (componentColor) {
    color = componentColor;
  }

  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);
  return { isImage, value, icon: getIcon(value), color, contrastColor };
};

const getLastModifiedByName = (
  componentDoc: SearchResultDoc & ComponentDoc
) => {
  if (componentDoc.ardoq.lastModifiedByName) {
    return componentDoc.ardoq.lastModifiedByName;
  }
  const user = orgUsersInterface.getUserById(componentDoc.lastModifiedBy);
  return user?.name ?? '';
};

const getCreatedByName = (componentDoc: SearchResultDoc & ComponentDoc) => {
  if (componentDoc.ardoq.createdByName) {
    return componentDoc.ardoq.createdByName;
  }
  const user = orgUsersInterface.getUserById(componentDoc.createdBy);
  return user?.name ?? '';
};

/**
 * `prototypeInterface` is part of the Backbone-era model interface layer, but it
 * reads no collection and no stream -- every member is an `advancedSearchApi` HTTP
 * call whose results are decorated with icon and colour helpers.
 *
 * API calls belong in a routine, not behind a model interface: dispatch an action
 * and do the fetch in `*routines.ts`, then let the result flow back through a
 * stream. Keeping them here hides an async side effect behind an interface that
 * callers reasonably expect to be a synchronous state read.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const prototypeInterface = {
  /**
   * @deprecated Call `advancedSearchApi` from a `*routines.ts` and emit the result through a stream.
   */
  loadComponentsFromAdvancedSearch,
  /**
   * @deprecated Call `advancedSearchApi` from a `*routines.ts` and emit the result through a stream.
   */
  loadComponentsByIdFromAdvancedSearch,
  /**
   * @deprecated Call `advancedSearchApi` from a `*routines.ts` and emit the result through a stream.
   */
  loadComponentsFromQueryBuilder,
};
